import { ProtocolCommand } from '../service-model/protocol-command.interface';
import { ProtocolQuery } from '../service-model/protocol-query.interface';
import { SHELL_PROTOCOL_OPERATIONS_NS } from './operations/shell-protocol-namespace.const';

// Collars (CR-20260529-001) shell operations + reply DTOs.
// Operation names must equal the FLAT backend op type names
// `ETS.ShellService.Shell.Protocol.Operations.<Name>` (ADR-014/020).
// Reply DTOs are camelCase to match the ShellRouter REST wire casing.

export interface CollarsBucketDto {
    bucketId: string;
    parentBucketId: string | null;
    level: number;            // 0 = Portfolio, 1 = Combo, 2 = Group
    name: string;
    ownerTerminalId: string;
    isArchived: boolean;
    sessionPnL: number;
    accumulatedPnL: number;
    // Combo settings (CR-20260611-001); meaningful only on Combo-level buckets. Backend-persisted on the
    // bucket row (replaces the per-user 'collars.combo-default-qty.*' user-setting — books are
    // per-terminal, so everyone viewing the book sees the same values).
    defaultQty: number | null;        // null = never set (consumers fall back to 1)
    autoExpireOptions: boolean;       // true = expired open options settle automatically at session reset
    hasStartingCapital: boolean;
    startingCapital: number | null;
    usableCashPercent: number;
    freeCash: number;
    spendableCash: number;
    accountValue: number;
    // Interest charge settings (WB-17; redesigned 2026-06-03); meaningful only on the "Interest" Group.
    // interestRate is a decimal fraction (0.08 = 8%); the dialog edits/shows it as a percent. There is no
    // live anchor — interest is a DISCRETE scheduled charge; the item shows the posted total, static
    // between charges.
    borrowedAmount: number;
    interestRate: number;
    daysInYear: number;
    chargeInterval: number;           // 0 = Daily, 1 = Weekly
    chargeDayOfWeek: number | null;   // System.DayOfWeek 0=Sun..6=Sat; null for Daily
    chargeTimeOfDay: string | null;   // "HH:mm" local
    chargeTimeZoneId: string | null;  // IANA zone id
    chargeCountry: string | null;     // label only
}

export interface CollarsTerminalDto {
    terminalId: string;
    displayName: string;
    // Live label of the user assigned to this seat (e.g. "Tom B."); falls back to displayName server-side.
    userName: string;
    isProxy: boolean;
}

export class ListCollarsBuckets implements ProtocolQuery {
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.ListCollarsBuckets`;
    }
}

export class GetCollarsAccessibleTerminals implements ProtocolQuery {
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.GetCollarsAccessibleTerminals`;
    }
}

export class CreateCollarsPortfolio implements ProtocolCommand {
    constructor(public readonly name: string, public readonly terminalId: string) { }
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.CreateCollarsPortfolio`;
    }
}

export class CreateCollarsCombo implements ProtocolCommand {
    constructor(
        public readonly name: string,
        public readonly terminalId: string,
        public readonly portfolioBucketId: string,
        public readonly template: string,
        public readonly startingCapital: number
    ) { }
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.CreateCollarsCombo`;
    }
}

export class CreateCollarsGroup implements ProtocolCommand {
    constructor(
        public readonly name: string,
        public readonly terminalId: string,
        public readonly comboBucketId: string
    ) { }
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.CreateCollarsGroup`;
    }
}

export class DeleteCollarsBucket implements ProtocolCommand {
    constructor(public readonly bucketId: string, public readonly terminalId: string) { }
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.DeleteCollarsBucket`;
    }
}

// ---- M2: positions, ledger, transaction recording + live push DTOs ----

export interface CollarsPositionDto {
    positionId: string;
    terminalId: string;
    tradingInstrumentCode: string;
    kind: number;                 // 0 = Asset, 1 = Option
    portfolioId: string | null;
    comboId: string | null;
    groupId: string | null;
    netPosition: number;
    avgPx: number;
    openPx: number | null;
    lastMatchPx: number;
    // Signed current market value (owner visual-request 2026-10-01): open net × current price ×
    // engine multiplier, computed backend-side. 0 for Interest/flat rows (the grid blanks those).
    marketValue: number;
    sessionRealizedPnL: number;
    sessionUnrealizedPnL: number;
    settledPnL: number;
    sessionTotalPnL: number;
    accumulatedTotalPnL: number;
    isArchived: boolean;
    // UTC archive timestamp (WB-18); null while active. The query-path date reviver rehydrates it to a Date.
    archivedDate: string | Date | null;
    isFlat: boolean;
    // True for the synthetic "Interest" item (WB-17): Realized-only accrual, no floating/quote/net.
    isInterest: boolean;
    // True only on a live push that signals the position was removed from the index (absorbed by a
    // With-P&L transfer merge); the panel drops the row. Absent on query results.
    isRemoved?: boolean;
}

export interface CollarsLedgerDto {
    recordId: string;
    sequence: number;
    positionId: string;
    terminalId: string;
    transactionType: number;      // CollarsTransactionType ordinal (labels: COLLARS_TXN_LABELS below)
    transactionDate: string | Date;
    tradingInstrumentCode: string;
    price: number;
    cashAmount: number;
    quantity: number;
    netPosition: number;
    positionAvgPx: number;
    portfolioId: string | null;
    portfolioName: string;
    comboId: string | null;
    comboName: string;
    groupId: string | null;
    groupName: string;
    itemPnL: number;
    groupPnL: number;
    comboPnL: number;
    portfolioPnL: number;
    sessionId: string | null;
    note: string;
}

// Display labels for CollarsTransactionType, INDEXED BY ENUM VALUE (labels[i] = ordinal i).
// The backend enum (Collars/Model/CollarsTransactionType.cs) only APPENDS members so existing
// ordinals never move (protobuf + this index-by-value array) — when a member is appended there,
// append its label here. Single shared copy — do not duplicate per component.
export const COLLARS_TXN_LABELS: ReadonlyArray<string> = [
    'Open', 'Increase', 'Decrease', 'Close', 'Reverse', 'Interest', 'Session P&L',
    'Transfer Out (With P&L)', 'Transfer In (With P&L)', 'Transfer Out (No P&L)', 'Transfer In (No P&L)',
    'Expiration', 'Assigned', 'Exercised', 'Override',
    '', 'Deposit', 'Withdrawal' // 15 is retired; backend cash rows use 16/17.
];

// Live ESS push payloads (messageType = the backend DTO class name; payload extends the
// query DTO with the ESS Topic = owning terminal).
export interface CollarsPositionChangedDto extends CollarsPositionDto {
    topic: string;
}

export interface CollarsLedgerChangedDto extends CollarsLedgerDto {
    topic: string;
}

export class ListCollarsPositions implements ProtocolQuery {
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.ListCollarsPositions`;
    }
}

export class GetCollarsLedger implements ProtocolQuery {
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.GetCollarsLedger`;
    }
}

export class RecordCollarsAccountCash implements ProtocolCommand {
    constructor(
        public readonly terminalId: string,
        public readonly comboBucketId: string,
        public readonly kind: number, // 1 = deposit, 2 = withdrawal
        public readonly amount: number
    ) { }
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.RecordCollarsAccountCash`;
    }
}

export class RecordCollarsTransaction implements ProtocolCommand {
    constructor(
        public readonly terminalId: string,
        public readonly groupBucketId: string,
        public readonly kind: number,                  // 0 = Asset, 1 = Option
        public readonly tradingInstrumentCode: string, // fully-composed code (asset symbol or option ticker)
        public readonly qty: number,                   // positive magnitude
        public readonly side: number,                  // 1 = Buy, -1 = Sell
        public readonly avgPx: number,
        // Optional user-supplied trade date/time as a UTC ISO string (…Z). null => backend stamps now.
        public readonly transactionDate: string | null = null
    ) { }
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.RecordCollarsTransaction`;
    }
}

// WB-17 (redesigned 2026-06-03): set the interest-charge settings on an "Interest" Group. interestRate is
// a decimal fraction (the dialog divides the entered percent by 100). borrowedAmount = 0 stops charging.
// chargeInterval: 0 = Daily, 1 = Weekly. chargeDayOfWeek: System.DayOfWeek (0=Sun..6=Sat), required for
// Weekly, null for Daily. chargeTimeOfDay: "HH:mm" local; chargeTimeZoneId: IANA id; chargeCountry: label.
export class SetCollarsInterestSettings implements ProtocolCommand {
    constructor(
        public readonly terminalId: string,
        public readonly groupBucketId: string,
        public readonly borrowedAmount: number,
        public readonly interestRate: number,
        public readonly daysInYear: number,
        public readonly chargeInterval: number,
        public readonly chargeDayOfWeek: number | null,
        public readonly chargeTimeOfDay: string,
        public readonly chargeTimeZoneId: string,
        public readonly chargeCountry: string
    ) { }
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.SetCollarsInterestSettings`;
    }
}

// Manual interest charge (owner request 2026-06-12): an EXTRA charge posted NOW for the WHOLE days
// elapsed since the group's last interest charge event (a first-ever charge posts one full configured
// period). The scheduled charge job is untouched. The backend REJECTS when nothing is due (not
// borrowing / rate 0 / less than one full day since the last charge) — a visible failure, never a
// silent no-op.
export class ChargeCollarsInterest implements ProtocolCommand {
    constructor(
        public readonly terminalId: string,
        public readonly groupBucketId: string
    ) { }
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.ChargeCollarsInterest`;
    }
}

// Preview for the manual-charge confirmation dialog — READ-ONLY. The backend computes the period and
// amount with the SAME code the charge runs (the fact-log "last charge" + the emulator-aware clock live
// backend-side, so the frontend never re-derives them). A not-due preview is a normal reply carrying the
// plain-language reason — the one channel that gets a backend reason to the user (command faults don't).
export class PreviewCollarsInterestCharge implements ProtocolQuery {
    constructor(
        public readonly terminalId: string,
        public readonly groupBucketId: string
    ) { }
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.PreviewCollarsInterestCharge`;
    }
}

export interface CollarsInterestChargePreviewDto {
    isDue: boolean;        // false = nothing due; the dialog is skipped and `reason` is shown instead
    chargeDays: number;    // whole days the charge covers (full configured period for a first charge)
    amount: number;        // NEGATIVE (a financing cost); 0 when not due
    reason: string | null; // plain-language not-due reason; null when due
}

// CR-20260611-001: set the per-Combo settings (Combo-level only; full-replace — every save carries every
// field). defaultQty >= 1; autoExpireOptions gates the automatic expiry settlement at session reset.
export class SetCollarsComboSettings implements ProtocolCommand {
    constructor(
        public readonly terminalId: string,
        public readonly comboBucketId: string,
        public readonly defaultQty: number,
        public readonly autoExpireOptions: boolean,
        public readonly startingCapital: number | null = null
    ) { }
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.SetCollarsComboSettings`;
    }
}

// Transfer (move-to-bucket) P&L policy — mirrors the backend CollarsTransferMode (Split omitted from v1).
export const CollarsTransferMode = { WithPnL: 1, WithoutPnL: 2 };

// Move one position to a target Group bucket under a P&L policy (1 = With P&L, 2 = Without P&L).
// Same-terminal only; records a Transfer Out / Transfer In ledger pair (enter-only).
export class TransferCollarsPosition implements ProtocolCommand {
    constructor(
        public readonly terminalId: string,
        public readonly sourcePositionId: string,
        public readonly targetGroupBucketId: string,
        public readonly mode: number
    ) { }
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.TransferCollarsPosition`;
    }
}

// Move every open position in a source Group to a target Group (one Out/In pair each).
export class TransferCollarsGroup implements ProtocolCommand {
    constructor(
        public readonly terminalId: string,
        public readonly sourceGroupBucketId: string,
        public readonly targetGroupBucketId: string,
        public readonly mode: number
    ) { }
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.TransferCollarsGroup`;
    }
}

// Archive one CLOSED (flat) position: it leaves Active and becomes a frozen, browsable Archived record
// (no order routed, no ledger row). One-way (no unarchive); the next trade on the same instrument/bucket
// opens a brand-new active position (the discriminator is freed). Same-terminal (authorised server-side).
export class ArchiveCollarsPosition implements ProtocolCommand {
    constructor(
        public readonly terminalId: string,
        public readonly positionId: string
    ) { }
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.ArchiveCollarsPosition`;
    }
}

// Archive a bucket (Portfolio / Combo / Group): cascades IsArchived over the WHOLE subtree (like delete)
// and auto-archives every CLOSED position in it. Blocked while the subtree holds an OPEN position or a
// borrowing Interest group (repay to 0 first). One-way (no unarchive); an archived bucket is READ-ONLY —
// the backend rejects every later write targeting it (owner rulings 2026-06-10).
export class ArchiveCollarsBucket implements ProtocolCommand {
    constructor(
        public readonly terminalId: string,
        public readonly bucketId: string
    ) { }
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.ArchiveCollarsBucket`;
    }
}

// Override a position's quantity and/or average price (enter-only, a manual correction — not a trade).
// Either field is optional: pass null to leave it unchanged (override qty and/or avg). The resolved
// net != 0 iff the resolved avg != 0. Same-terminal (authorised server-side).
export class OverrideCollarsPosition implements ProtocolCommand {
    constructor(
        public readonly terminalId: string,
        public readonly positionId: string,
        public readonly netPosition: number | null,
        public readonly avgPx: number | null
    ) { }
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.OverrideCollarsPosition`;
    }
}

// Settle an option position at expiry (enter-only): close the option leg (Expiration) and, for
// Assigned (short) / Exercised (long), spawn the underlying at the strike, netting any held asset.
// Same-terminal. outcome mirrors the backend CollarsExpiryOutcome: 0 = Worthless, 1 = Assigned, 2 = Exercised.
// outcome mirrors the backend CollarsExpiryOutcome: 0 = Worthless, 1 = Assigned, 2 = Exercised,
// 3 = Cash Settled (index). settlementPrice = the underlying settlement level, used only for Cash
// Settled (the option closes at its intrinsic vs the strike); 0 for the other outcomes.
// CR-20260611-001: the outcome is DERIVED from settlementPrice (the underlying settlement level) vs the
// strike + net sign — never chosen by the user. The dialog supplies only the level.
export class SettleCollarsOptionExpiry implements ProtocolCommand {
    constructor(
        public readonly terminalId: string,
        public readonly optionPositionId: string,
        public readonly settlementPrice: number
    ) { }
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.SettleCollarsOptionExpiry`;
    }
}

// Preview an option's expiry settlement for the Settle-at-Expiry dialog (CR-20260611-001) — read-only.
// Resolves the underlying settlement level for the expiration day (overrideLevel null = mark -> fetch;
// a value = derive from that), and returns moneyness + the derived outcome + a plain description.
export class PreviewCollarsOptionExpiry implements ProtocolQuery {
    constructor(
        public readonly terminalId: string,
        public readonly optionPositionId: string,
        public readonly overrideLevel: number | null
    ) { }
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.PreviewCollarsOptionExpiry`;
    }
}

export interface CollarsOptionExpiryPreviewDto {
    optionPositionId: string;
    net: number;
    strike: number;
    optionType: string;            // 'Call' | 'Put'
    isCashSettled: boolean;
    settleTimeUtc: Date;           // the 4:20 PM ET settle instant (reviver parses 'time' fields to Date)
    levelResolved: boolean;        // false => the dialog must collect the level from the user
    settlementLevel: number;
    levelSource: string;           // 'Operator mark' | 'Historical fetch' | 'Entered' | 'Unavailable'
    isInTheMoney: boolean;
    moneynessLabel: string;        // 'In the money' | 'At the money' | 'Out of the money' (Eps-tolerant, backend-derived)
    intrinsic: number;             // per-unit intrinsic value; 0 when not in the money
    realizedPnL: number;           // net (total) P&L the settle WILL book (the SAME number the ledger records); premium already netted in
    premiumAmount: number;         // breakdown term: premium component, signed (+ received / - paid). market (operation) P&L is derived on the client as realizedPnL - premiumAmount
    outcome: number;               // derived CollarsExpiryOutcome (0 Worthless/1 Assigned/2 Exercised/3 CashSettled)
    outcomeLabel: string;
    description: string;
    spawnsUnderlying: boolean;
    underlyingCode: string;
    underlyingQty: number;
    underlyingSide: string;        // 'Buy' | 'Sell'
    underlyingPrice: number;
}

// ---- Retro-fill (Backdate) — preview + commit (retro-fill re-architecture) ----
// A backdated fill is a SERIOUS operation: it re-derives every session and open position after its
// insertion point. The blotter is forward-only; backdating goes through Preview (a dry run that shows the
// consequences) then Commit (with any operator-supplied prices). Names equal the flat backend op names.

export interface CollarsPriceNeedDto {
    tradingInstrumentCode: string;
    groupBucketId: string;
    asOfUtc: string | Date;
}

export interface CollarsSessionDeltaDto {
    sessionId: string;
    endDate: string | Date;
    sessionPnLBefore: number;
    sessionPnLAfter: number;
    delta: number;
}

export interface CollarsPositionDeltaDto {
    tradingInstrumentCode: string;
    groupId: string | null;
    groupName: string;
    netBefore: number;
    netAfter: number;
    avgPxBefore: number;
    avgPxAfter: number;
    accumulatedBefore: number;
    accumulatedAfter: number;
    isNew: boolean;
}

// One expiry settlement the commit will SYNTHESIZE for an option leg already past expiration
// (retro-expired options): the leg retro-fills, retro-expires and retro-settles atomically.
// Outcome wire values = backend CollarsExpiryOutcome: 0 Worthless, 1 Assigned, 2 Exercised, 3 CashSettled.
// settleTimeUtc = expiration date 4:20 PM Eastern as UTC (the reviver date-converts it — correct, it IS
// an instant); 'outcome'/'settlementPrice' carry no 'time'/'date' tokens (reviver-safe numerics).
export interface CollarsSyntheticExpiryDto {
    optionInstrumentCode: string;
    groupBucketId: string;
    underlyingCode: string;
    settleTimeUtc: string | Date;
    outcome: number;
    settlementPrice: number;
}

// The computed consequences of a proposed backdated fill (dry run — the server writes nothing).
export interface CollarsRetroPreviewDto {
    isRetro: boolean;
    blockedReason: string | null;
    canCommit: boolean;
    lifetimeBefore: number;
    lifetimeAfter: number;
    ledgerRowsAfter: number;
    priceNeeds: CollarsPriceNeedDto[];
    sessionChanges: CollarsSessionDeltaDto[];
    positionChanges: CollarsPositionDeltaDto[];
    // Settlements the commit will synthesize for retro-expired option legs. The settlement level is
    // operator-overridable: a changed value commits as a manual mark for the UNDERLYING at
    // settleTimeUtc (the same marks array price-needs already use).
    syntheticExpiries: CollarsSyntheticExpiryDto[];
}

// One operator-supplied price for a recompute hole (a past instant a position now spans).
export interface CollarsManualMarkInput {
    tradingInstrumentCode: string;
    groupBucketId: string;
    price: number;
    asOfUtc: string;             // UTC ISO
}

// Dry-run a backdated fill and return its consequences — WRITES NOTHING. The dialog calls this first.
export class PreviewRetroFill implements ProtocolQuery {
    constructor(
        public readonly terminalId: string,
        public readonly groupBucketId: string,
        public readonly kind: number,                  // 0 = Asset, 1 = Option
        public readonly tradingInstrumentCode: string,
        public readonly qty: number,                   // positive magnitude
        public readonly side: number,                  // 1 = Buy, -1 = Sell
        public readonly avgPx: number,
        public readonly transactionDate: string | null // the (past) trade time as a UTC ISO string
    ) { }
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.PreviewRetroFill`;
    }
}

// Commit a backdated fill after the operator confirmed the preview + supplied any needed prices.
export class CommitRetroFill implements ProtocolCommand {
    constructor(
        public readonly terminalId: string,
        public readonly groupBucketId: string,
        public readonly kind: number,
        public readonly tradingInstrumentCode: string,
        public readonly qty: number,
        public readonly side: number,
        public readonly avgPx: number,
        public readonly transactionDate: string | null,
        public readonly marks: CollarsManualMarkInput[]
    ) { }
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.CommitRetroFill`;
    }
}

// ---- Multi-leg retro batches (CR-20260610-001, ADR-023) ----
// A structure (e.g. a collar) backdated as ONE atomic operation: one combined preview, then a commit
// where every leg books or none does. Legs may target different Groups (same terminal) and each leg
// carries its OWN trade time — REQUIRED; the Backdate dialog is the dated entry (no null-means-now).
// The single-leg ops above remain functional (expand-migrate-contract; single = batch of one).

export interface CollarsRetroLegInput {
    groupBucketId: string;
    kind: number;                  // 0 = Asset, 1 = Option
    tradingInstrumentCode: string; // fully-composed code (asset symbol or option ticker)
    qty: number;                   // positive magnitude
    side: number;                  // 1 = Buy, -1 = Sell
    avgPx: number;
    tradeTimeUtc: string;          // REQUIRED — the leg's trade time as a UTC ISO string
}

// Dry-run a multi-leg batch and return ONE combined preview across the whole leg set — WRITES NOTHING.
// isRetro is true when ANY leg lands before the book's latest event.
export class PreviewRetroFills implements ProtocolQuery {
    constructor(
        public readonly terminalId: string,
        public readonly legs: CollarsRetroLegInput[]
    ) { }
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.PreviewRetroFills`;
    }
}

// Commit a multi-leg batch atomically after the combined preview + any operator-supplied prices.
export class CommitRetroFills implements ProtocolCommand {
    constructor(
        public readonly terminalId: string,
        public readonly legs: CollarsRetroLegInput[],
        public readonly marks: CollarsManualMarkInput[]
    ) { }
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.CommitRetroFills`;
    }
}

// ---- Session P&L history (CR collars-session-pnl) ----
// Snapshots (frozen per-item P&L at reset) and the trade ledger are TWO datasets, both keyed by SessionId.
// The dialog clones the ETS Session History layout; the far-right-bottom pane is a single flat ledger.

// Left-pane session list row. Closed sessions come from stored headers; the active (not-yet-reset)
// session is a synthesized anchor row (isActive = true, endDate = null, no P&L, pinned on top,
// non-drillable — owner ruling 2026-06-12).
export interface CollarsSessionDto {
    sessionId: string;
    terminalId: string;
    terminalName: string;
    // Live label of the user assigned to this terminal (e.g. "Tom B."); the UI groups/shows this.
    userName: string;
    startDate: string | Date;
    endDate: string | Date | null;
    sessionPnL: number;
    accumulatedPnL: number;
    isActive: boolean;
}

// One per-item session snapshot (the middle roll-up source + the far-right-top item list). The frontend
// rolls portfolio/combo/group totals up from this flat list. Frozen at reset — never live.
export interface CollarsSessionSnapshotDto {
    snapshotId: string;
    sessionId: string;
    terminalId: string;
    positionId: string;
    tradingInstrumentCode: string;
    kind: number;                 // 0 = Asset, 1 = Option
    portfolioId: string | null;
    portfolioName: string;
    comboId: string | null;
    comboName: string;
    groupId: string | null;
    groupName: string;
    netPosition: number;
    avgPx: number;
    lastMatchPx: number;
    sessionPnL: number;
    accumulatedPnL: number;
    snapshotDate: string | Date;
}

// Live ESS push when a session is reset + its history captured; the open dialog reloads its session list.
export interface CollarsSessionEndedDto {
    topic: string;
    terminalId: string;
    endedSessionId: string;
    newSessionId: string;
    sessionPnL: number;
    accumulatedPnL: number;
    endDate: string | Date;
}

// Session list (closed sessions + one ACTIVE anchor row per accessible terminal), scoped to accessible
// terminals. The active row carries NO P&L (owner ruling 2026-06-12 — the dialog never shows live session
// P&L; the frontend renders its P&L cells blank). With bucketId (the bucket-scoped dialog): only the
// bucket's owner terminal, each closed session's P&L re-summed server-side over the bucket subtree's
// snapshots, closed sessions with no subtree data omitted. Omitted = the unscoped browser (unchanged).
export class GetCollarsSessions implements ProtocolQuery {
    constructor(public readonly bucketId?: string) { }
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.GetCollarsSessions`;
    }
}

// Per-item snapshots for one session: the frozen stored snapshots of a closed session. An active
// session id returns empty — the active row is non-drillable (owner ruling 2026-06-12).
export class GetCollarsSessionSnapshots implements ProtocolQuery {
    constructor(public readonly sessionId: string) { }
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.GetCollarsSessionSnapshots`;
    }
}

// Flat (non-drillable) ledger rows for one session, optionally narrowed to a bucket or a single position.
// Reuses CollarsLedgerDto for the reply.
export class GetCollarsSessionLedger implements ProtocolQuery {
    constructor(
        public readonly sessionId: string,
        public readonly bucketId?: string,
        public readonly positionId?: string
    ) { }
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.GetCollarsSessionLedger`;
    }
}

// Reset a terminal's session from the dialog — SUPER-USER ONLY (enforced server-side, on top of terminal-access
// scoping). The handler (ResetCollarsSessionCH) runs the INDEPENDENT TMHQ reset (ICollarsSessionService.ResetSession)
// directly — it does NOT re-dispatch the ETS-CORE platform reset (the original design; replaced 2026-06-04 after its
// nested-impersonation 401). Scheduled resets reach the same service via BeforeSessionResetMessage (reset-TIME trigger).
export class ResetCollarsSession implements ProtocolCommand {
    constructor(public readonly terminalId: string) { }
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.ResetCollarsSession`;
    }
}


// Snapshot plus events for the Portfolio Hedging history window. Ordering fields are additive.
export interface CollarsHistoryEventMetadata { streamGeneration: string; eventRevision: number; }
export interface CollarsHistoryPositionEvent extends CollarsPositionChangedDto, CollarsHistoryEventMetadata {
    sessionId: string;
    hadActivityThisSession: boolean;
    portfolioName?: string;
    comboName?: string;
    groupName?: string;
}
export interface CollarsHistoryLedgerEvent extends CollarsLedgerChangedDto, CollarsHistoryEventMetadata { }
export interface CollarsHistoryBoundaryEvent extends CollarsHistoryEventMetadata { terminalId: string; }
export interface CollarsHistoryItemDto {
    sessionId: string; positionId: string; terminalId: string; tradingInstrumentCode: string; kind: number;
    portfolioId: string | null; portfolioName: string; comboId: string | null; comboName: string;
    groupId: string | null; groupName: string; netPosition: number; avgPx: number; lastMatchPx: number;
    sessionPnL: number; accumulatedPnL: number; isInterest: boolean;
}
export interface CollarsTerminalHistoryDto {
    terminalId: string; streamGeneration: string; capturedRevision: number; sessions: CollarsSessionDto[];
    activeItems: CollarsHistoryItemDto[]; selectedSessionId: string | null;
    selectedItems: CollarsHistoryItemDto[] | null; ledger: CollarsLedgerDto[] | null;
}
export interface CollarsSessionHistoryStateDto { terminals: CollarsTerminalHistoryDto[]; }
export class GetCollarsSessionHistoryState implements ProtocolQuery {
    constructor(public readonly terminalIds?: string[], public readonly scopeBucketId?: string,
        public readonly selectedSessionId?: string, public readonly ledgerBucketId?: string,
        public readonly positionId?: string) { }
    getOperationName(): string { return `${SHELL_PROTOCOL_OPERATIONS_NS}.GetCollarsSessionHistoryState`; }
}
